import { TextWithIconButton } from "components/parts/Button/TextWithIconButton";
import { TextWithIconTypes } from "components/parts/Button/TextWithIconButton/TextWithIconButton";
import { StatusLabel } from "components/parts/Label/StatusLabel";
import { Typography } from "components/parts/Typography/Typography";
import { DisplayInterview } from "pages/InterviewManagement/hook";
import {
  StyledInterviewDetailCard,
  StyledInterviewDetailContent,
  StyledInterviewDetailContentRow1,
  StyledInterviewFlow,
  StyledInterviewFlowContent,
  StyledInterviewHeader,
  StyledInterviewHeaderTitleWrapper,
  StyledInterviewHeaderWrapper,
  StyledMemo,
  StyledMemoContent,
  StyledMonitor,
  StyledMonitorContent,
  StyledTitleAndOpenButtonDiv,
  StyledTitleAndOpenAndEditButtonDiv,
  StyledNoDataTypography,
  StyledAttributeNameTypography,
  StyledAttributeValueTypography,
  StyledMonitorAttributeContent,
  StyledMonitorInfoArea,
  StyledTransitionAdjuster,
  StyledTriangleLeft,
  StyledMonitorWithInfoAdjuster,
  StyledMonitorWithInfo,
  StyledMonitorWithIsLogin,
  StyledCheckCircle,
} from "pages/InterviewManagement/interviewDetail/styled";
import React from "react";
import { CreateChatTokenInput, InterviewType, Monitor } from "types/graphqlapi";
import { createModeratorChatTokenIdQuery } from "utils/apis/chatToken";
import {
  getDisplayYMD,
  getDisplayYMDhm,
  getModeratorInterviewUrl,
} from "utils/common";
import { useConsoleState } from "providers/ConsoleStateProvider";
import { useLoadingState } from "providers/LoadingProvider";
import { useSenarioTemplateSet } from "pages/ScenarioManagement/ScenarioExpand/hook";
import { ScenarioPreview } from "pages/ScenarioManagement/ScenarioExpand";

type InterviewDetailProps = {
  selectInterview: (interview: DisplayInterview) => void;
  selectedInterviewDetail: DisplayInterview | undefined;
};

export const InterviewDetail: React.FC<InterviewDetailProps> = ({
  selectInterview,
  selectedInterviewDetail,
}) => {
  const { userId, selectedGroupId, selectedSubjectId, selectedSubjectType } =
    useConsoleState();
  const { scenarioTemplateSet } = useSenarioTemplateSet();
  const { setLoading } = useLoadingState();

  const openInterviewPage = async (selectedInterviewId: string) => {
    setLoading(true);
    const input: CreateChatTokenInput = {
      subject_id: selectedSubjectId,
      interview_id: selectedInterviewId,
      moderator_id: userId,
      groups: selectedGroupId,
    };
    try {
      await createModeratorChatTokenIdQuery(input);
      window.open(`${getModeratorInterviewUrl(userId, selectedInterviewId)}`);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const [selectedMonitor, setSelectedMonitor] = React.useState<Monitor[]>([]);

  return (
    <StyledInterviewDetailCard>
      {selectedInterviewDetail ? (
        <>
          <StyledInterviewHeaderWrapper>
            <StyledInterviewHeader>
              <StyledInterviewHeaderTitleWrapper>
                <StatusLabel status={selectedInterviewDetail.status} />
                <StyledTitleAndOpenAndEditButtonDiv>
                  <StyledTitleAndOpenButtonDiv>
                    <Typography
                      size="xlarge"
                      text={`${selectedInterviewDetail.interview_seq}`}
                    />
                    <Typography
                      size="xlarge"
                      text={
                        selectedSubjectType === InterviewType.TERM
                          ? `${
                              selectedInterviewDetail.scheduled_start_date &&
                              selectedInterviewDetail.scheduled_end_date
                                ? `${getDisplayYMD(
                                    new Date(
                                      selectedInterviewDetail.scheduled_start_date
                                    )
                                  )} ${
                                    selectedInterviewDetail.scheduled_start_time
                                  } ～ ${getDisplayYMD(
                                    new Date(
                                      selectedInterviewDetail.scheduled_end_date
                                    )
                                  )} ${
                                    selectedInterviewDetail.scheduled_end_time
                                  }`
                                : ""
                            }`
                          : `${getDisplayYMD(
                              new Date(
                                selectedInterviewDetail.scheduled_start_date as string
                              )
                            )} ${
                              selectedInterviewDetail.scheduled_start_time
                            }～${selectedInterviewDetail.scheduled_end_time}`
                      }
                    />
                    <TextWithIconButton
                      onClick={() =>
                        openInterviewPage(selectedInterviewDetail.id)
                      }
                      text={
                        selectedInterviewDetail.status === "DONE" ||
                        selectedInterviewDetail.canceled !== 0
                          ? "インタビュー表示"
                          : "入室"
                      }
                      variant="primary"
                      iconName={TextWithIconTypes.ExternalLinkOutline}
                    />
                  </StyledTitleAndOpenButtonDiv>
                  <TextWithIconButton
                    onClick={() => selectInterview(selectedInterviewDetail)}
                    text="編集"
                    variant="secondary"
                    iconName={TextWithIconTypes.Edit}
                  />
                </StyledTitleAndOpenAndEditButtonDiv>
              </StyledInterviewHeaderTitleWrapper>
            </StyledInterviewHeader>
          </StyledInterviewHeaderWrapper>
          <StyledInterviewDetailContent>
            <StyledInterviewDetailContentRow1>
              <StyledMonitor>
                <Typography text="参加モニター：" weight="bold" />
                {selectedInterviewDetail.monitorList.length === 0 ? (
                  <StyledMonitorContent>
                    <Typography text={"未アサイン"} size="small" />
                  </StyledMonitorContent>
                ) : (
                  selectedInterviewDetail.monitorList.map((monitor, index) => (
                    <StyledMonitorContent key={`${monitor.id}${index}`}>
                      <StyledMonitorWithInfoAdjuster>
                        <StyledMonitorWithInfo>
                          <StyledMonitorWithIsLogin>
                            <Typography
                              text={monitor.name ? monitor.name : "未設定"}
                            />
                            <Typography
                              text={"事前ログイン"}
                              size="xsmall"
                              color="text"
                            />
                            {monitor.isMonitorLogin ? (
                              <StyledCheckCircle />
                            ) : (
                              <Typography
                                text={"なし"}
                                size="xsmall"
                                color="text"
                              />
                            )}
                            <Typography
                              text={`最終投稿：${
                                monitor.lastSentDate
                                  ? getDisplayYMDhm(
                                      new Date(monitor.lastSentDate)
                                    )
                                  : "なし"
                              }`}
                              size="xsmall"
                              color="text"
                            />
                          </StyledMonitorWithIsLogin>
                          <StyledMonitorInfoArea>
                            <Typography
                              text={`${monitor.sex || "未設定"} | ${
                                monitor.age || "未設定"
                              } | ${monitor.job || "未設定"} | ${
                                monitor.marriage || "未設定"
                              } | ${monitor.children || "未設定"} | ${
                                monitor.address || "未設定"
                              } | ${monitor.classification || "未設定"}`}
                              size="small"
                            />
                          </StyledMonitorInfoArea>
                        </StyledMonitorWithInfo>
                        <StyledTransitionAdjuster
                          selected={!selectedMonitor?.includes(monitor)}
                          onClick={() =>
                            setSelectedMonitor((prevMonitorList) => {
                              if (prevMonitorList.includes(monitor)) {
                                return prevMonitorList.filter(
                                  (prev) => prev !== monitor
                                );
                              } else return [...prevMonitorList, monitor];
                            })
                          }
                        >
                          <StyledTriangleLeft />
                        </StyledTransitionAdjuster>
                      </StyledMonitorWithInfoAdjuster>

                      <StyledMonitorInfoArea>
                        {selectedMonitor?.includes(monitor)
                          ? ""
                          : selectedInterviewDetail.monitorAttributeList.map(
                              (attributeList) =>
                                attributeList
                                  .filter(
                                    (attribute) =>
                                      attribute.monitor_id === monitor.id
                                  )
                                  .map((attribute, index) => (
                                    <StyledMonitorAttributeContent
                                      key={`${attribute.id}${index}`}
                                    >
                                      <StyledAttributeNameTypography
                                        text={attribute.name}
                                        size="small"
                                      />
                                      <StyledAttributeValueTypography
                                        text={
                                          attribute.value
                                            ? attribute.value
                                            : "未設定"
                                        }
                                        size="small"
                                      />
                                    </StyledMonitorAttributeContent>
                                  ))
                            )}
                      </StyledMonitorInfoArea>
                    </StyledMonitorContent>
                  ))
                )}
              </StyledMonitor>
              <StyledMemo>
                <Typography text="備考：" weight="bold" />
                <StyledMemoContent>
                  <Typography
                    text={selectedInterviewDetail.memo || ""}
                    size="small"
                  />
                </StyledMemoContent>
              </StyledMemo>
            </StyledInterviewDetailContentRow1>
            <StyledInterviewFlow>
              <Typography text="インタビューフロー：" weight="bold" />
              <StyledInterviewFlowContent>
                {scenarioTemplateSet[0].scenario.id ? (
                  <ScenarioPreview scenarioTemplateSet={scenarioTemplateSet} />
                ) : (
                  <StyledNoDataTypography
                    text="設定されていません"
                    size="small"
                  />
                )}
              </StyledInterviewFlowContent>
            </StyledInterviewFlow>
          </StyledInterviewDetailContent>
        </>
      ) : (
        <Typography text="実査を選択してください" size="large" />
      )}
    </StyledInterviewDetailCard>
  );
};
